import React, { useState } from "react";
import { Box, Button, IconButton, Stack, Textarea, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import type {
  ActionRequest,
  AssistantStep,
  AssistantTurn,
  Decision,
  PendingApprovalData,
  ReviewConfig,
  UserTurn,
} from "./types";

// =========================
// AgentLivingLogo — "살아있는 뇌" (top-down view) MBCT/CBT × Entity feel
//   · 뇌 실루엣 (좌우 hemisphere) — 3겹으로 그려서 와이어 두께감
//   · 종단 fissure — 중앙 골이 호흡하며 흐름
//   · gyri 곡선 (뇌주름) — 각 hemisphere 안에서 살짝 흔들림
//   · 내부에 떠도는 신경 입자 12개 — 다른 속도, 뇌 안 범위 안에 머무름
//   · 중심 코어 — 다층 halo + flicker (의식의 가운데)
//   · 가끔 sweep 라인이 한 바퀴 돎 (알아차림 스캔)
//   모든 timing 이 lockstep 이 아니라 organic.
// =========================

const BRAIN_OUTLINE =
  "M 40 12 C 30 9, 17 14, 12 26 C 7 38, 10 52, 19 60 C 26 66, 35 66, 40 62 C 45 66, 54 66, 61 60 C 70 52, 73 38, 68 26 C 63 14, 50 9, 40 12 Z";

const BRAIN_FISSURE = "M 40 12 C 38 22, 42 30, 40 40 C 38 50, 42 58, 40 62";

const GYRI_LEFT = [
  "M 16 30 Q 22 27, 28 31 T 36 29",
  "M 14 42 Q 22 39, 28 44 T 36 42",
  "M 18 52 Q 24 49, 30 54",
  "M 20 22 Q 26 19, 32 22",
];

const GYRI_RIGHT = [
  "M 64 30 Q 58 27, 52 31 T 44 29",
  "M 66 42 Q 58 39, 52 44 T 44 42",
  "M 62 52 Q 56 49, 50 54",
  "M 60 22 Q 54 19, 48 22",
];

export function AgentLivingLogo({
  size = 44,
  active = true,
}: {
  size?: number;
  active?: boolean;
}) {
  const primary = theme.vars.palette.primary.solid;
  const soft = theme.vars.palette.primary.soft;

  // 16 신경 입자 — 각자 고유한 chaotic trajectory
  //   주요 회전 + 부 sinusoidal 각도 jitter + 반경 진동 합성
  //   서로 다른 주기/위상이 겹쳐서 lockstep 절대 안 됨
  const particles = Array.from({ length: 16 }, (_, i) => ({
    baseR: 6 + ((i * 2.7) % 14), // 기본 반경
    rAmp: 2 + ((i * 0.8) % 5), // 반경 진동 amplitude
    rFreq: 1 + (i % 3), // 반경 sinusoid 주파수 (정수, loop 매끄럽게)
    angAmp: 18 + ((i * 7) % 32), // 각속도 비균질성 (degrees)
    angFreq: 1 + ((i + 1) % 3), // 각도 jitter 주파수 (정수)
    dur: 5.5 + ((i * 1.3) % 9), // 한 cycle 시간
    startAngle: (i * 137.5) % 360, // golden-angle spacing
    phase: (i * 0.41) % (Math.PI * 2), // sinusoid 위상 offset
    size: 0.45 + ((i * 0.13) % 0.7),
    opacity: 0.4 + ((i * 0.07) % 0.5),
    dir: i % 2 === 0 ? 1 : -1,
  }));

  // 각 입자별 keyframes 생성 — 12 stops 의 chaotic 궤도
  const particleAnimations: Record<string, unknown> = {};
  const NUM_STOPS = 12;
  particles.forEach((p, i) => {
    const kfName = `agent-pt-${i}`;
    const stops: Record<string, { transform: string }> = {};
    for (let j = 0; j <= NUM_STOPS; j++) {
      const t = j / NUM_STOPS;
      // 각속도: 균일 회전 + sinusoidal jitter
      const angleDeg =
        p.startAngle +
        t * 360 * p.dir +
        Math.sin(t * Math.PI * 2 * p.angFreq + p.phase) * p.angAmp;
      // 반경: base + sinusoidal 진동
      const r =
        p.baseR + Math.sin(t * Math.PI * 2 * p.rFreq + p.phase * 1.7) * p.rAmp;
      const rad = (angleDeg * Math.PI) / 180;
      const x = r * Math.cos(rad);
      const y = r * Math.sin(rad);
      stops[`${Math.round(t * 100)}%`] = {
        transform: `translate(${x.toFixed(2)}px, ${y.toFixed(2)}px)`,
      };
    }
    particleAnimations[`@keyframes ${kfName}`] = stops;
  });

  return (
    <Box
      component="svg"
      viewBox="0 0 80 80"
      sx={{
        width: size * (80 / 44),
        height: size * (80 / 44),
        overflow: "visible",
        ...particleAnimations,
        "@keyframes agent-brain-breath": {
          "0%, 100%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.035)" },
        },
        "@keyframes agent-brain-breath-slow": {
          "0%, 100%": { transform: "scale(1)" },
          "50%": { transform: "scale(1.02)" },
        },
        "@keyframes agent-gyri-fade": {
          "0%, 100%": { opacity: 0.35 },
          "50%": { opacity: 0.75 },
        },
        "@keyframes agent-gyri-fade-2": {
          "0%, 100%": { opacity: 0.55 },
          "50%": { opacity: 0.25 },
        },
        "@keyframes agent-fissure-flow": {
          "0%, 100%": { strokeDashoffset: 0, opacity: 0.55 },
          "50%": { strokeDashoffset: -6, opacity: 0.85 },
        },
        "@keyframes agent-core-pulse": {
          "0%, 100%": { transform: "scale(1)", opacity: 1 },
          "50%": { transform: "scale(1.22)", opacity: 0.85 },
        },
        "@keyframes agent-halo-pulse": {
          "0%, 100%": { transform: "scale(1)", opacity: 0.25 },
          "50%": { transform: "scale(1.4)", opacity: 0.08 },
        },
        "@keyframes agent-flicker": {
          "0%, 96%, 100%": { opacity: 1 },
          "97%": { opacity: 0.55 },
          "98%": { opacity: 0.9 },
          "99%": { opacity: 0.6 },
        },
        "@keyframes agent-sweep": {
          "0%": { transform: "rotate(0deg)", opacity: 0 },
          "5%, 8%": { opacity: 0.5 },
          "15%, 100%": { opacity: 0, transform: "rotate(360deg)" },
        },
        "@keyframes agent-firing": {
          "0%, 90%, 100%": { opacity: 0 },
          "92%, 96%": { opacity: 1 },
        },
        "& .brain-outer": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-brain-breath 4.2s ease-in-out infinite"
            : "none",
        },
        "& .brain-mid": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-brain-breath-slow 5.6s ease-in-out infinite"
            : "none",
        },
        "& .brain-inner": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-brain-breath 3.4s ease-in-out infinite"
            : "none",
        },
        "& .fissure": {
          animation: active
            ? "agent-fissure-flow 4.2s ease-in-out infinite"
            : "none",
        },
        "& .gyri-L": {
          animation: active
            ? "agent-gyri-fade 3.6s ease-in-out infinite"
            : "none",
        },
        "& .gyri-R": {
          animation: active
            ? "agent-gyri-fade-2 4.4s ease-in-out infinite"
            : "none",
        },
        ...Object.fromEntries(
          particles.map((p, i) => [
            `& .pt-${i}`,
            {
              animation: active
                ? `agent-pt-${i} ${p.dur}s linear infinite`
                : "none",
            },
          ])
        ),
        "& .core-pulse": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-core-pulse 3.2s ease-in-out infinite"
            : "none",
        },
        "& .core-halo": {
          transformOrigin: "40px 40px",
          animation: active
            ? "agent-halo-pulse 3.2s ease-in-out infinite"
            : "none",
        },
        "& .core-flicker": {
          animation: active ? "agent-flicker 6s ease-in-out infinite" : "none",
        },
        "& .sweep": {
          transformOrigin: "40px 40px",
          animation: active ? "agent-sweep 9s linear infinite" : "none",
        },
        "& .firing-1": {
          animation: active ? "agent-firing 5s ease-out infinite" : "none",
        },
        "& .firing-2": {
          animation: active ? "agent-firing 7s ease-out infinite 2.3s" : "none",
        },
        "& .firing-3": {
          animation: active ? "agent-firing 6s ease-out infinite 4.1s" : "none",
        },
      }}
    >
      {/* 뇌 실루엣 3겹 (외곽 / 중간 / 안쪽) — 와이어 두께감 */}
      <g className="brain-outer">
        <path
          d={BRAIN_OUTLINE}
          fill={primary}
          opacity="0.04"
          stroke={soft}
          strokeWidth="0.6"
          strokeOpacity="0.4"
        />
      </g>
      <g className="brain-mid">
        <path
          d={BRAIN_OUTLINE}
          fill="none"
          stroke={primary}
          strokeWidth="0.7"
          opacity="0.55"
        />
      </g>
      <g
        className="brain-inner"
        transform="translate(40 40) scale(0.93) translate(-40 -40)"
      >
        <path
          d={BRAIN_OUTLINE}
          fill="none"
          stroke={primary}
          strokeWidth="0.45"
          opacity="0.35"
          strokeDasharray="1.5 1.5"
        />
      </g>

      {/* sweep 라인 — 알아차림 스캔 */}
      <g className="sweep">
        <line
          x1="40"
          y1="40"
          x2="40"
          y2="10"
          stroke={primary}
          strokeWidth="0.4"
          opacity="0.45"
        />
      </g>

      {/* 좌우 hemisphere gyri (뇌주름) */}
      <g className="gyri-L" stroke={primary} strokeWidth="0.5" fill="none">
        {GYRI_LEFT.map((d, i) => (
          <path key={`gL-${i}`} d={d} />
        ))}
      </g>
      <g className="gyri-R" stroke={primary} strokeWidth="0.5" fill="none">
        {GYRI_RIGHT.map((d, i) => (
          <path key={`gR-${i}`} d={d} />
        ))}
      </g>

      {/* 종단 fissure (중앙 골) — 흐르는 dash */}
      <path
        className="fissure"
        d={BRAIN_FISSURE}
        fill="none"
        stroke={primary}
        strokeWidth="0.75"
        strokeDasharray="2 1.5"
        opacity="0.55"
      />

      {/* 신경 입자 16개 — 각자 고유한 chaotic trajectory (Lissajous 합성) */}
      {particles.map((p, i) => (
        <circle
          key={`pt-${i}`}
          className={`pt-${i}`}
          cx="40"
          cy="40"
          r={p.size}
          fill={primary}
          opacity={p.opacity}
        />
      ))}

      {/* synaptic firing — 가끔 깜빡이는 점 3개 */}
      <circle
        className="firing-1"
        cx="26"
        cy="32"
        r="1"
        fill={primary}
        opacity="0"
      />
      <circle
        className="firing-2"
        cx="54"
        cy="38"
        r="1.1"
        fill={primary}
        opacity="0"
      />
      <circle
        className="firing-3"
        cx="48"
        cy="52"
        r="0.9"
        fill={primary}
        opacity="0"
      />

      {/* 중심 코어 — 의식 */}
      <g className="core-flicker">
        <g className="core-halo">
          <circle cx="40" cy="40" r="13" fill={primary} opacity="0.07" />
        </g>
        <g className="core-halo">
          <circle cx="40" cy="40" r="8" fill={primary} opacity="0.14" />
        </g>
        <g className="core-pulse">
          <circle cx="40" cy="40" r="4.5" fill={primary} opacity="0.35" />
          <circle cx="40" cy="40" r="2.6" fill={primary} />
        </g>
      </g>
    </Box>
  );
}

// =========================
// PresetScenarios — clickable bubbles to send canned messages
// =========================

export interface PresetScenario {
  id: string;
  label: string;
  message: string;
  description?: string;
}

export const PRESET_SCENARIOS: PresetScenario[] = [
  {
    id: "greet",
    label: "안녕",
    message: "안녕! 어떤 활동지 만들 수 있어?",
    description: "간단한 인사 — task 변경 없음",
  },
  {
    id: "create",
    label: "새 활동지",
    message: "첫 활동지 만들어줘",
    description: "가이드 read 후 write_file approval",
  },
  {
    id: "title",
    label: "제목 바꾸기",
    message: "제목을 다르게 바꿔줘",
    description: "edit_file old/new approval (현재 task 필요)",
  },
  {
    id: "add",
    label: "블록 추가",
    message: "블록 하나 추가해줘",
    description: "LastCard 직전에 새 Instruction 삽입",
  },
  {
    id: "help",
    label: "도움말",
    message: "도움말",
    description: "사용 가능한 작업 안내",
  },
];

export function PresetScenariosBar({
  onPick,
  disabled,
}: {
  onPick: (scenario: PresetScenario) => void;
  disabled: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: "6px",
        px: "12px",
        pt: "8px",
        pb: "4px",
        borderTop: `1px solid ${theme.vars.palette.Border.Weak}`,
      }}
    >
      <Typography
        level="body-xs"
        sx={{
          fontSize: "10px",
          color: theme.vars.palette.Content.Weak,
          alignSelf: "center",
          mr: "2px",
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        시나리오
      </Typography>
      {PRESET_SCENARIOS.map((scenario) => (
        <Box
          key={scenario.id}
          onClick={() => !disabled && onPick(scenario)}
          title={scenario.description}
          sx={{
            px: "10px",
            py: "4px",
            borderRadius: "999px",
            backgroundColor: theme.vars.palette.Background.Level2,
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            fontSize: "11px",
            fontWeight: 500,
            color: theme.vars.palette.Content.Medium,
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.5 : 1,
            transition: "all 0.15s ease",
            userSelect: "none",
            "&:hover": disabled
              ? {}
              : {
                  backgroundColor: theme.vars.palette.Background.Level1,
                  borderColor: theme.vars.palette.Content.Medium,
                  color: theme.vars.palette.Content.Strong,
                  transform: "translateY(-1px)",
                },
          }}
        >
          {scenario.label}
        </Box>
      ))}
    </Box>
  );
}

// =========================
// UserBubble
// =========================

export function UserBubble({ turn }: { turn: UserTurn }) {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        animation: "fadeInUp 0.25s ease",
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(4px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box
        sx={{
          maxWidth: "85%",
          px: "12px",
          py: "8px",
          borderRadius: "14px 14px 4px 14px",
          backgroundColor: theme.vars.palette.primary.solid,
          color: "#fff",
          fontSize: "13px",
          lineHeight: 1.5,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        }}
      >
        {turn.content}
      </Box>
    </Box>
  );
}

// =========================
// ToolCallRow — line status with expandable result
// =========================

function summarizeToolArgs(
  name: string,
  args: Record<string, unknown>
): string {
  if (typeof args.file_path === "string") {
    const parts = args.file_path.split("/");
    return parts[parts.length - 1] || args.file_path;
  }
  if (typeof args.path === "string") return args.path;
  return Object.keys(args).slice(0, 2).join(", ") || "";
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

export function ToolCallRow({
  step,
}: {
  step: Extract<AssistantStep, { kind: "tool" }>;
}) {
  const [expanded, setExpanded] = useState(false);
  const running = step.status === "running";
  const argsSummary = summarizeToolArgs(step.name, step.args);
  const duration =
    step.finishedAt && step.startedAt
      ? formatDuration(step.finishedAt - step.startedAt)
      : null;
  const hasResult = !!step.result;

  return (
    <Box
      onClick={() => hasResult && setExpanded((v) => !v)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "2px",
        px: "8px",
        py: "5px",
        borderRadius: "6px",
        cursor: hasResult ? "pointer" : "default",
        transition: "background-color 0.15s",
        "&:hover": hasResult
          ? { backgroundColor: theme.vars.palette.Background.Level2 }
          : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: "12px",
        }}
      >
        <Box
          sx={{
            width: "12px",
            height: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: running ? theme.vars.palette.primary.solid : "#15803d",
            flexShrink: 0,
          }}
        >
          {running ? (
            <Box
              sx={{
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                border: `1.5px solid ${theme.vars.palette.Background.Level3 ?? "#ddd"}`,
                borderTopColor: theme.vars.palette.primary.solid,
                animation: "spin 0.8s linear infinite",
                "@keyframes spin": {
                  to: { transform: "rotate(360deg)" },
                },
              }}
            />
          ) : (
            <Box
              component="span"
              sx={{ fontSize: "12px", lineHeight: 1, fontWeight: 700 }}
            >
              ✓
            </Box>
          )}
        </Box>
        <Box
          component="span"
          sx={{
            color: theme.vars.palette.Content.Strong,
            fontWeight: 600,
          }}
        >
          {step.name}
        </Box>
        <Box
          component="span"
          sx={{
            color: theme.vars.palette.Content.Weak,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          {argsSummary}
        </Box>
        {duration && (
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              color: theme.vars.palette.Content.Weak,
              flexShrink: 0,
            }}
          >
            {duration}
          </Box>
        )}
        {hasResult && (
          <Box
            component="span"
            sx={{
              fontSize: "10px",
              color: theme.vars.palette.Content.Weak,
              flexShrink: 0,
            }}
          >
            {expanded ? "▾" : "▸"}
          </Box>
        )}
      </Box>
      {expanded && step.result && (
        <Box
          sx={{
            mt: "6px",
            ml: "20px",
            p: "8px 10px",
            borderRadius: "4px",
            backgroundColor: theme.vars.palette.Background.Level2,
            border: `1px solid ${theme.vars.palette.Border.Weak}`,
            fontFamily:
              "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
            fontSize: "11px",
            lineHeight: 1.5,
            color: theme.vars.palette.Content.Medium,
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
            maxHeight: "200px",
            overflow: "auto",
          }}
        >
          {step.result}
        </Box>
      )}
    </Box>
  );
}

// =========================
// TextStreamBubble
// =========================

export function TextStreamBubble({
  step,
  isLast,
  isStreaming,
}: {
  step: Extract<AssistantStep, { kind: "text" }>;
  isLast: boolean;
  isStreaming: boolean;
}) {
  return (
    <Box
      sx={{
        px: "12px",
        py: "8px",
        borderRadius: "4px 14px 14px 14px",
        backgroundColor: theme.vars.palette.Surface.Forward,
        border: `1px solid ${theme.vars.palette.Border.Weak}`,
        fontSize: "13px",
        lineHeight: 1.6,
        color: theme.vars.palette.Content.Strong,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        animation: "fadeIn 0.2s ease",
        "@keyframes fadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      {step.content}
      {isLast && isStreaming && (
        <Box
          component="span"
          sx={{
            display: "inline-block",
            width: "6px",
            height: "13px",
            backgroundColor: theme.vars.palette.Content.Strong,
            verticalAlign: "text-bottom",
            ml: "2px",
            animation: "blink 1s steps(2) infinite",
            "@keyframes blink": {
              "0%, 50%": { opacity: 1 },
              "50.01%, 100%": { opacity: 0 },
            },
          }}
        />
      )}
    </Box>
  );
}

// =========================
// ApprovalDecisionStep — 사용자가 내린 결정 표시
// =========================

export function ApprovalDecisionRow({
  step,
}: {
  step: Extract<AssistantStep, { kind: "approval_decision" }>;
}) {
  const decisionLabel =
    step.decision.type === "approve"
      ? "✓ 승인"
      : step.decision.type === "edit"
        ? "✎ 수정 후 적용"
        : "✕ 거부";
  const decisionColor =
    step.decision.type === "approve"
      ? "#15803d"
      : step.decision.type === "edit"
        ? theme.vars.palette.primary.solid
        : "#b91c1c";
  const rejectMessage =
    step.decision.type === "reject" ? step.decision.message : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        px: "8px",
        py: "4px",
        fontSize: "11px",
        color: theme.vars.palette.Content.Weak,
      }}
    >
      <Box
        component="span"
        sx={{
          color: decisionColor,
          fontWeight: 700,
        }}
      >
        {decisionLabel}
      </Box>
      <Box
        component="span"
        sx={{
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          color: theme.vars.palette.Content.Medium,
        }}
      >
        {step.originalAction.name}
      </Box>
      {rejectMessage && (
        <Box
          component="span"
          sx={{
            fontStyle: "italic",
            color: theme.vars.palette.Content.Weak,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
            minWidth: 0,
          }}
        >
          “{rejectMessage}”
        </Box>
      )}
    </Box>
  );
}

// =========================
// ApprovalCard
// =========================

export function ApprovalCard({
  data,
  onDecide,
}: {
  data: PendingApprovalData;
  onDecide: (decisions: Decision[]) => void;
}) {
  return (
    <Stack
      spacing={1.5}
      sx={{
        p: "12px",
        mt: "4px",
        borderRadius: "8px",
        backgroundColor: theme.vars.palette.Background.Level1,
        border: `1px solid ${theme.vars.palette.primary.solid}`,
        animation: "approvalPop 0.3s ease",
        "@keyframes approvalPop": {
          from: { opacity: 0, transform: "scale(0.98)" },
          to: { opacity: 1, transform: "scale(1)" },
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          sx={{
            width: "6px",
            height: "6px",
            borderRadius: "50%",
            backgroundColor: theme.vars.palette.primary.solid,
            animation: "pulse 1.5s ease infinite",
            "@keyframes pulse": {
              "0%, 100%": { opacity: 1 },
              "50%": { opacity: 0.4 },
            },
          }}
        />
        <Typography
          level="body-xs"
          sx={{
            fontSize: "11px",
            fontWeight: 700,
            color: theme.vars.palette.primary.solid,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          승인 필요
        </Typography>
      </Stack>
      {data.action_requests.map((action, idx) => (
        <ApprovalItem
          key={idx}
          action={action}
          config={data.review_configs[idx]}
          onApprove={() => onDecide([{ type: "approve" }])}
          onEdit={(editedArgs) =>
            onDecide([
              {
                type: "edit",
                edited_action: { name: action.name, args: editedArgs },
              },
            ])
          }
          onReject={(message) => onDecide([{ type: "reject", message }])}
        />
      ))}
    </Stack>
  );
}

function ApprovalItem({
  action,
  config,
  onApprove,
  onEdit,
  onReject,
}: {
  action: ActionRequest;
  config?: ReviewConfig;
  onApprove: () => void;
  onEdit: (args: Record<string, unknown>) => void;
  onReject: (message: string) => void;
}) {
  const [mode, setMode] = useState<"idle" | "edit" | "reject">("idle");
  const [argsText, setArgsText] = useState(() =>
    JSON.stringify(action.args, null, 2)
  );
  const [rejectMessage, setRejectMessage] = useState("");

  const allowed = config?.allowed_decisions ?? ["approve", "edit", "reject"];

  return (
    <Stack spacing={1}>
      <Box
        sx={{
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: "12px",
          color: theme.vars.palette.Content.Strong,
          fontWeight: 600,
        }}
      >
        {action.name}
        <Box
          component="span"
          sx={{
            ml: "8px",
            color: theme.vars.palette.Content.Medium,
            fontWeight: 400,
          }}
        >
          {typeof action.args.file_path === "string"
            ? String(action.args.file_path)
            : ""}
        </Box>
      </Box>

      <Typography
        level="body-sm"
        sx={{
          fontSize: "13px",
          color: theme.vars.palette.Content.Medium,
          lineHeight: 1.5,
        }}
      >
        {action.description}
      </Typography>

      <ActionArgsPreview args={action.args} />

      {mode === "idle" && (
        <Stack direction="row" spacing={1}>
          {allowed.includes("approve") && (
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={onApprove}
              sx={{ minHeight: "30px", fontSize: "12px", fontWeight: 600 }}
            >
              ✓ Approve
            </Button>
          )}
          {allowed.includes("edit") && (
            <Button
              size="sm"
              variant="outlined"
              color="neutral"
              onClick={() => setMode("edit")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ✎ Edit
            </Button>
          )}
          {allowed.includes("reject") && (
            <Button
              size="sm"
              variant="plain"
              color="danger"
              onClick={() => setMode("reject")}
              sx={{ minHeight: "30px", fontSize: "12px" }}
            >
              ✕ Reject
            </Button>
          )}
        </Stack>
      )}

      {mode === "edit" && (
        <Stack spacing={1}>
          <Textarea
            value={argsText}
            onChange={(e) => setArgsText(e.target.value)}
            minRows={6}
            maxRows={14}
            sx={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "11px",
            }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="solid"
              color="primary"
              onClick={() => {
                try {
                  const parsed = JSON.parse(argsText);
                  onEdit(parsed);
                } catch {
                  // ignore parse error — leave UI open
                }
              }}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              수정 후 적용
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
          </Stack>
        </Stack>
      )}

      {mode === "reject" && (
        <Stack spacing={1}>
          <Textarea
            value={rejectMessage}
            onChange={(e) => setRejectMessage(e.target.value)}
            placeholder="왜 거부하시나요? agent 에게 전달됩니다"
            minRows={2}
            maxRows={5}
            sx={{ fontSize: "12px" }}
          />
          <Stack direction="row" spacing={1}>
            <Button
              size="sm"
              variant="solid"
              color="danger"
              onClick={() => onReject(rejectMessage.trim())}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              거부 전송
            </Button>
            <Button
              size="sm"
              variant="plain"
              color="neutral"
              onClick={() => setMode("idle")}
              sx={{ minHeight: "28px", fontSize: "12px" }}
            >
              취소
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

function ActionArgsPreview({ args }: { args: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  const isWriteFile = typeof args.content === "string";
  const isEditFile =
    typeof args.old_string === "string" && typeof args.new_string === "string";

  if (isEditFile) {
    return (
      <Box>
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            color: theme.vars.palette.Content.Weak,
            cursor: "pointer",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            mb: "4px",
            userSelect: "none",
          }}
        >
          {expanded ? "▾" : "▸"} diff
        </Box>
        {expanded && (
          <Box
            sx={{
              borderRadius: "4px",
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              backgroundColor: theme.vars.palette.Background.Level2,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "11px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: "6px 8px",
                backgroundColor: "rgba(239, 68, 68, 0.08)",
                color: "#b91c1c",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
                borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
              }}
            >
              − {String(args.old_string)}
            </Box>
            <Box
              sx={{
                p: "6px 8px",
                backgroundColor: "rgba(34, 197, 94, 0.08)",
                color: "#15803d",
                whiteSpace: "pre-wrap",
                wordBreak: "break-all",
              }}
            >
              + {String(args.new_string)}
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  if (isWriteFile) {
    const content = String(args.content);
    return (
      <Box>
        <Box
          onClick={() => setExpanded((v) => !v)}
          sx={{
            fontSize: "10px",
            fontWeight: 600,
            color: theme.vars.palette.Content.Weak,
            cursor: "pointer",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            mb: "4px",
            userSelect: "none",
          }}
        >
          {expanded ? "▾" : "▸"} content ({content.length} chars)
        </Box>
        {expanded && (
          <Box
            sx={{
              p: "8px 10px",
              borderRadius: "4px",
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              backgroundColor: theme.vars.palette.Background.Level2,
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "11px",
              lineHeight: 1.5,
              maxHeight: "240px",
              overflow: "auto",
              whiteSpace: "pre",
            }}
          >
            {content}
          </Box>
        )}
      </Box>
    );
  }

  return null;
}

// =========================
// TaskPatchPill
// =========================

export function TaskPatchPill() {
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "4px",
        alignSelf: "flex-start",
        px: "8px",
        py: "3px",
        mt: "2px",
        borderRadius: "999px",
        backgroundColor: theme.vars.palette.primary.soft,
        color: theme.vars.palette.primary.solid,
        fontSize: "10px",
        fontWeight: 600,
        letterSpacing: "0.02em",
        animation: "patchSlide 0.3s ease",
        "@keyframes patchSlide": {
          from: { opacity: 0, transform: "translateY(-2px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      <Box component="span">✎</Box>
      활동지 업데이트됨
    </Box>
  );
}

// =========================
// DebugFooter
// =========================

export function DebugFooter({
  debug,
}: {
  debug: NonNullable<AssistantTurn["debug"]>;
}) {
  const copyTrace = () => {
    if (debug.trace_id && typeof navigator !== "undefined") {
      navigator.clipboard?.writeText(debug.trace_id);
    }
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        mt: "4px",
        fontSize: "10px",
        color: theme.vars.palette.Content.Weak,
        fontFamily:
          "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
      }}
    >
      <Box component="span">⏱ {formatDuration(debug.execution_time_ms)}</Box>
      {debug.trace_id && (
        <Box
          component="span"
          onClick={copyTrace}
          title="클릭해서 trace id 복사"
          sx={{
            cursor: "pointer",
            "&:hover": { color: theme.vars.palette.Content.Medium },
          }}
        >
          · trace {debug.trace_id}
        </Box>
      )}
    </Box>
  );
}

// =========================
// AssistantTurnView
// =========================

export function AssistantTurnView({
  turn,
  isLast,
  isStreaming,
  onDecide,
}: {
  turn: AssistantTurn;
  isLast: boolean;
  isStreaming: boolean;
  onDecide: (decisions: Decision[]) => void;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        animation: "fadeInUp 0.25s ease",
        "@keyframes fadeInUp": {
          from: { opacity: 0, transform: "translateY(4px)" },
          to: { opacity: 1, transform: "translateY(0)" },
        },
      }}
    >
      {turn.steps.map((step, idx) => {
        const isLastStep = idx === turn.steps.length - 1;
        if (step.kind === "tool") {
          return <ToolCallRow key={step.id} step={step} />;
        }
        if (step.kind === "text") {
          return (
            <TextStreamBubble
              key={step.id}
              step={step}
              isLast={isLast && isLastStep}
              isStreaming={isStreaming && isLast && isLastStep}
            />
          );
        }
        return <ApprovalDecisionRow key={step.id} step={step} />;
      })}

      {turn.status === "awaiting_approval" && turn.pendingApproval && (
        <ApprovalCard data={turn.pendingApproval} onDecide={onDecide} />
      )}

      {turn.taskChanged && <TaskPatchPill />}

      {turn.error && (
        <Box
          sx={{
            p: "8px 10px",
            borderRadius: "6px",
            backgroundColor: "rgba(239, 68, 68, 0.08)",
            border: `1px solid rgba(239, 68, 68, 0.3)`,
            fontSize: "12px",
            color: "#b91c1c",
          }}
        >
          ✕ {turn.error.message}
        </Box>
      )}

      {turn.debug && <DebugFooter debug={turn.debug} />}
    </Box>
  );
}

// =========================
// ChatInput
// =========================

export function ChatInput({
  value,
  onChange,
  onSubmit,
  disabled,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: () => void;
  disabled: boolean;
  placeholder?: string;
}) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      if (!disabled && value.trim()) onSubmit();
    }
  };
  return (
    <Box
      sx={{
        p: "10px 12px 12px",
        display: "flex",
        alignItems: "flex-end",
        gap: "8px",
        backgroundColor: theme.vars.palette.Surface.Forward,
      }}
    >
      <Textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        minRows={1}
        maxRows={5}
        disabled={disabled}
        sx={{
          flex: 1,
          fontSize: "13px",
          borderRadius: "8px",
        }}
      />
      <IconButton
        variant="solid"
        color="primary"
        onClick={onSubmit}
        disabled={disabled || !value.trim()}
        sx={{
          minHeight: "36px",
          minWidth: "36px",
          fontSize: "16px",
          borderRadius: "8px",
        }}
        title="전송 (Enter)"
      >
        ↑
      </IconButton>
    </Box>
  );
}
